import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { visibleListingsForDate } from '../../../util/search';
import { ListingCard, ListingCardSkeleton, PaginationLinks } from '../../../components';
import NoAvailableResultsMaybe from '../NoAvailableResultsMaybe/NoAvailableResultsMaybe';

import css from './SearchResultsPanel.module.css';

// Enough placeholders to fill the grid at every column count it uses - 2, 3, 4 and 5 across - so
// the page keeps its height while results are on their way instead of collapsing onto the footer.
const SKELETON_CARD_COUNT = 12;

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {Array<string>|null} [props.availableListingIds] - ids free on the searched date, or
 *   null while unresolved (no date, probe in flight, or probe failed)
 * @param {boolean} [props.showSkeletons] - stand placeholders in for results that are on their
 *   way, rather than showing the cards of a search the visitor has left
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    availableListingIds = null,
    showSkeletons = false,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  // One rule, one place: util/search.js owns which listings a dated search shows, because the
  // map variant plots its pins from the same set and the two must never disagree.
  const visibleListings = visibleListingsForDate(listings, availableListingIds);

  // "Are there OTHER pages", not "are there LATER pages": on the last page of a multi-page set
  // the remaining pages are behind the visitor rather than ahead, and they are still somewhere
  // to look. Both messages claim only what was actually probed — this page — so neither
  // overclaims about a catalogue nobody asked the API about.
  const hasOtherPages = pagination?.totalPages > 1;

  // Every card on the page was hidden. Nothing else catches this: NoSearchResultsMaybe fires on
  // `totalItems === 0`, and totalItems counts the unfiltered superset the main query returned —
  // so without this the header reads "42 Ergebnisse" above a band of blank whitespace and the
  // visitor is left to guess why.
  //
  // Guarded on there having been listings to hide, so an empty page from any other cause (a
  // search that genuinely matched nothing) still belongs to NoSearchResultsMaybe.
  //
  // While placeholders stand in for the results, what the probe hid from the previous search's
  // cards says nothing about the results on their way, so the message stays out of it.
  const hidAllListings = !showSkeletons && listings.length > 0 && visibleListings.length === 0;

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 50vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      {hidAllListings ? <NoAvailableResultsMaybe hasOtherPages={hasOtherPages} /> : null}
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {showSkeletons
          ? Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
              <ListingCardSkeleton className={css.listingCard} key={`skeleton-${i}`} />
            ))
          : visibleListings.map(l => (
              <ListingCard
                className={css.listingCard}
                key={l.id.uuid}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
              />
            ))}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
